/**
 * Prose recovered from a file that has no printed page: docx, odt, epub, html, rtf, plain
 * text, email.
 *
 * Blocks are rendered as what they are — a heading at its real depth, a list at its real
 * indent, an aside as a margin note — rather than as the flattened Markdown `/content`
 * serves. That flattening is deliberate and lossy on the read path; this is the same parse
 * before it happened.
 */
import { DataTable, type Column } from "./DataTable";
import { Empty } from "./ui";
import type { DocumentStructure, StructureBlock } from "../lib/types";

const HEADING_CLASS: Record<number, string> = {
  1: "text-[19px] font-extrabold tracking-tight",
  2: "text-[16px] font-bold tracking-tight",
  3: "text-[14px] font-bold",
  4: "text-[13px] font-semibold",
  5: "text-[12.5px] font-semibold text-dim",
  6: "text-[12px] font-semibold text-dim",
};

function TableBlock({ block, index }: { block: Extract<StructureBlock, { kind: "table" }>; index: number }) {
  type Row = { cells: string[] };
  const columns: Column<Row>[] = block.header.map((label, i) => ({
    key: `c${i}`,
    header: label || " ",
    width: 180,
    render: (row: Row) => row.cells[i] ?? "",
  }));
  const rows: Row[] = block.rows.map((cells) => ({ cells }));
  return (
    <div className="my-3 border border-hair">
      <DataTable table={`docblock:${index}`} columns={columns} rows={rows} rowKey={(_, i) => String(i)} />
    </div>
  );
}

function Block({ block, index }: { block: StructureBlock; index: number }) {
  switch (block.kind) {
    case "heading":
      return (
        <h3 className={`mt-5 mb-2 ${HEADING_CLASS[block.level] ?? HEADING_CLASS[6]}`}>
          {block.text}
        </h3>
      );
    case "paragraph":
      return <p className="my-2 max-w-[78ch] text-[13.5px] leading-relaxed text-dim">{block.text}</p>;
    case "list_item":
      return (
        <div
          className="my-1 flex gap-2 text-[13.5px] leading-relaxed text-dim"
          style={{ paddingInlineStart: `${block.depth * 18}px` }}
        >
          <span className="text-faint select-none">·</span>
          <span>{block.text}</span>
        </div>
      );
    case "caption":
      return <p className="my-2 text-[12.5px] text-faint italic">{block.text}</p>;
    case "aside":
      return (
        <div className="my-3 border-s-2 border-gold ps-3">
          {block.label && <p className="eyebrow mb-0.5 text-faint">{block.label}</p>}
          <p className="text-[13px] leading-relaxed text-dim">{block.text}</p>
        </div>
      );
    case "image":
      return (
        <div className="my-3 grid h-20 place-items-center border border-dashed border-hair text-[12px] text-faint">
          {block.alt || "image with no extractable text"}
        </div>
      );
    case "table":
      return <TableBlock block={block} index={index} />;
  }
}

export function DocumentView({ structure }: { structure: DocumentStructure }) {
  if (structure.blocks.length === 0) {
    return <Empty title="nothing recovered" hint="the parse produced no blocks for this file" />;
  }
  const units = [...new Set(structure.blocks.map((placed) => placed.origin.unit))];
  const labelled = units.length > 1;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
      {units.map((unit) => (
        <section key={unit}>
          {labelled && (
            <p className="eyebrow mt-4 mb-1 border-b border-hair pb-1 text-faint">
              {structure.blocks.find((p) => p.origin.unit === unit)?.origin.unit_label ??
                `part ${unit + 1}`}
            </p>
          )}
          {structure.blocks
            .filter((placed) => placed.origin.unit === unit)
            .map((placed, i) => (
              <Block key={`${unit}-${i}`} block={placed.block} index={unit * 1000 + i} />
            ))}
        </section>
      ))}
    </div>
  );
}
